import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegister } from '../../api/queryHooks/user';
import { useSignupWithPasskey } from '../../api/queryHooks/mfa';
import { Loader } from '@f2m3/shared-frontend-ui';
import { isEmailAddress } from '../../utils/emailAddress';

interface RegisterProps {
  email: string;
  /** Back to the email step, to fix an address that can't be signed up. */
  onChangeEmail?: () => void;
}

const Register = ({ email, onChangeEmail }: RegisterProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const { mutate: register, isPending } = useRegister();
  const { mutate: signupPasskey, isPending: isPasskeyPending } = useSignupWithPasskey();

  const passkeySupported = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    register({ email, name, password });
  };

  // The login step takes any address (an existing account logs in whatever
  // its address looks like); a new account needs one that looks like email.
  // The server re-checks, and also turns away domains that take no mail.
  if (!isEmailAddress(email)) {
    return (
      <>
        <div className="notification is-warning is-light mb-4">
          {t('login.register_email_invalid', { email })}
        </div>
        {onChangeEmail && (
          <button type="button" className="button" onClick={onChangeEmail}>
            {t('login.change_email')}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <div className="notification is-info is-light mb-4">
        {t('login.register_intro')}
      </div>
      <div className="field">
        <label className="label">{t('login.email_label')}</label>
        <div className="control">
          <input className="input" type="email" autoComplete="username" value={email} disabled />
        </div>
      </div>
      <div className="field">
        <label className="label">{t('login.register_name_label')}</label>
        <div className="control">
          <input
            className="input"
            type="text"
            autoComplete="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder={t('login.register_name_placeholder')}
          />
        </div>
      </div>

      {passkeySupported && (
        <>
          <div className="field">
            <div className="control">
              <button
                type="button"
                className={`button is-primary is-fullwidth${isPasskeyPending ? ' is-loading' : ''}`}
                disabled={!name || isPasskeyPending}
                onClick={() => signupPasskey({ email, name })}
              >
                {t('login.passkey_signup_button')}
              </button>
            </div>
            <p className="help">{t('login.passkey_signup_hint')}</p>
          </div>
          <div className="is-flex is-align-items-center my-4">
            <hr className="is-flex-grow-1" />
            <span className="mx-3 has-text-grey is-size-7">{t('login.or_divider')}</span>
            <hr className="is-flex-grow-1" />
          </div>
        </>
      )}

      <form onSubmit={handleSubmit}>
        {/* Password managers look for the username in the password's own form, to save the new
            password under it (and, with new-password, offer to generate one). */}
        <input type="email" className="is-visually-hidden" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" />
        <div className="field">
          <label className="label">{t('login.register_password_label')}</label>
          <div className="control">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.register_password_placeholder')}
            />
          </div>
          <p className="help">{t('login.register_password_hint')}</p>
        </div>
        {isPending && <Loader />}
        {!isPending && (
          <div className="field">
            <div className="control has-text-right">
              <button
                className="button is-primary"
                type="submit"
                disabled={!name || !password}
              >
                {t('login.register_button')}
              </button>
            </div>
          </div>
        )}
      </form>
    </>
  );
}

export default Register;
